// src/services/authService.js
/**
 * SIMS Session & Authentication Security Service
 * Implements strict terminal session-key lifecycles:
 * 1. Fresh page loads on port 3000 always require login.
 * 2. Browser reloads/refreshes refresh (invalidate) the active session key.
 * 3. All protected routes redirect to /login whenever session key is refreshed or expired.
 */

const SESSION_KEY = 'sims_session_key';
const TOKEN_KEY = 'token';
const ROLE_KEY = 'role';
const USERNAME_KEY = 'sims_username';

let reloadHandled = false;

export function resetReloadHandledForTest() {
  reloadHandled = false;
}

/**
 * Checks if the current page load was triggered by a browser refresh/reload
 */
export function isBrowserReload() {
  if (typeof window === 'undefined') return false;
  try {
    const navEntries = performance.getEntriesByType('navigation');
    if (navEntries && navEntries.length > 0) {
      return navEntries[0].type === 'reload';
    }
    return window.performance?.navigation?.type === 1;
  } catch (e) {
    return false;
  }
}

/**
 * Mint a new unique session key
 */
export function generateSessionKey() {
  const rand = Math.random().toString(36).substring(2, 10);
  return `sess_${Date.now()}_${rand}`;
}

/**
 * Initialize an active authenticated session
 */
export function initSession(token, role = 'ROLE_CASHIER', username = 'user') {
  reloadHandled = true;
  const sessionKey = generateSessionKey();
  
  if (typeof window !== 'undefined') {
    // Store in both sessionStorage (session lifetime) and localStorage (backward compatibility)
    sessionStorage.setItem(SESSION_KEY, sessionKey);
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(ROLE_KEY, role);
    sessionStorage.setItem(USERNAME_KEY, username);

    localStorage.setItem(SESSION_KEY, sessionKey);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ROLE_KEY, role);
    localStorage.setItem(USERNAME_KEY, username);

    window.dispatchEvent(new Event('sims-auth-changed'));
  }

  return { sessionKey, token, role, username };
}

/**
 * Invalidate and clear current session
 */
export function clearSession() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(USERNAME_KEY);

    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem('sims_current_location_id');

    window.dispatchEvent(new Event('sims-auth-changed'));
  }
}

/**
 * Get active JWT token
 */
export function getToken() {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || null;
}

/**
 * Get active user role
 */
export function getUserRole() {
  if (typeof window === 'undefined') return 'ROLE_CASHIER';
  const payload = decodeJwtPayload(getToken());
  if (payload && payload.role) return payload.role;
  return sessionStorage.getItem(ROLE_KEY) || localStorage.getItem(ROLE_KEY) || 'ROLE_CASHIER';
}

/**
 * Decode a JWT payload segment. JWT payloads use base64url, so convert it to
 * standard base64 and restore padding before calling atob.
 */
function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

/**
 * Get the client id embedded in the active JWT, if any. The backend still
 * re-checks the persisted employee/client relationship on every request; this
 * value is only used to drive tenant-aware UI choices.
 */
export function getClientId() {
  if (typeof window === 'undefined') return null;
  const payload = decodeJwtPayload(getToken());
  return payload && payload.clientId != null ? String(payload.clientId) : null;
}

/**
 * Validate active session key and token
 * If refreshed, expired, or absent, returns false and cleans up
 */
export function checkSession() {
  if (typeof window === 'undefined') return false;

  // 1. If page was refreshed, invalidate the active session key from the pre-reload document
  if (isBrowserReload() && !reloadHandled) {
    reloadHandled = true;
    clearSession();
    return false;
  }

  // 2. Validate session key exists
  const sessionKey = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
  if (!sessionKey || sessionKey === 'undefined' || sessionKey === 'null') {
    clearSession();
    return false;
  }

  // 3. Validate token exists
  const token = getToken();
  if (!token || token === 'undefined' || token === 'null' || token === '') {
    clearSession();
    return false;
  }

  // 4. Validate JWT expiration
  const payload = decodeJwtPayload(token);
  if (!payload) {
    clearSession();
    return false;
  }
  if (payload.exp && payload.exp * 1000 < Date.now()) {
    clearSession();
    return false;
  }
  return true;
}

/**
 * Generate client-side JWT fallback for offline POS terminals when backend is down
 */
export function generateOfflineJwt(username = 'admin', role = 'ROLE_ADMIN') {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(
    JSON.stringify({
      sub: username,
      role: role,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600 * 4, // 4 hours
    })
  );
  return `${header}.${payload}.sims_offline_sig`;
}

const authService = {
  initSession,
  clearSession,
  checkSession,
  getToken,
  getUserRole,
  getClientId,
  isBrowserReload,
  generateSessionKey,
  generateOfflineJwt,
  resetReloadHandledForTest,
};

export default authService;
